import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { MODULES, SETTINGS_MODULE } from "@/lib/modules";
import { useApp } from "@/context/AppContext";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ChevronLeft } from "lucide-react";
import { IrisBrand } from "@/components/common/IrisBrand";

export function IconRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useApp();
  const [expanded, setExpanded] = useState(false);

  const moduleKeyOfPath = (path) => {
    if (path === "/") return "dashboard";
    return path.split("/")[1];
  };
  const activeKey = location.pathname.startsWith("/settings") || location.pathname === "/profile"
    ? "settings"
    : moduleKeyOfPath(location.pathname);

  const items = MODULES.filter((m) => can(m.key, "view"));

  const RailButton = ({ m }) => {
    const active = activeKey === m.key;
    const Icon = m.icon;
    const btn = (
      <button
        data-testid={`rail-${m.key}`}
        onClick={() => navigate(m.path)}
        className={cn(
          "group relative flex items-center rounded-lg text-navy-300 transition-colors",
          expanded ? "w-full gap-3 px-3 py-2.5" : "h-9 w-9 justify-center mx-auto",
          active ? "bg-[#3A86FF] text-white" : "hover:bg-navy-700 hover:text-white"
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" />
        {expanded && <span className="truncate text-[13px] font-medium">{m.label}</span>}
      </button>
    );
    if (expanded) return btn;
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        <TooltipContent side="right">{m.label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
      <aside
        className={cn("flex h-screen flex-col bg-navy-900 py-2 transition-[width] duration-200",
          expanded ? "w-[220px] px-3" : "w-16 px-2.5")}
      >
        <div className={cn("mb-3", expanded ? "px-1" : "flex justify-center")}>
          {expanded ? (
            <IrisBrand
              className="items-start text-left gap-1"
              iconClassName="h-6 w-6 text-[#3A86FF]"
              titleClassName="text-base text-white"
              subClassName="text-navy-300"
              taglineClassName="text-xs text-navy-300"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#3A86FF] font-display text-lg font-bold text-white shrink-0">
              I
            </div>
          )}
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          {items.map((m) => <RailButton key={m.key} m={m} />)}
        </nav>

        <div className="mt-1.5 space-y-1 border-t border-navy-700 pt-1.5">
          {can("settings", "view") && <RailButton m={SETTINGS_MODULE} />}
          <button
            data-testid="rail-collapse-toggle"
            onClick={() => setExpanded((e) => !e)}
            className={cn("flex items-center rounded-lg text-navy-300 hover:bg-navy-700 hover:text-white",
              expanded ? "w-full gap-3 px-3 py-2" : "h-9 w-9 justify-center mx-auto")}
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", !expanded && "rotate-180")} />
            {expanded && <span className="text-[13px]">Collapse</span>}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
